import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/core/services/toast.service';
import { environment } from 'src/environments/environment';

interface DemandeDevis {
  id: number;
  villeDepart: string;
  villeArrivee: string;
  dateEnvoi: string;
  statut: string;
  user?: { id: number; nom?: string; prenom?: string; email?: string; telephone?: string };
  articles?: any[];
  dateCreation?: string;
}

const STATUTS: Record<string, { libelle: string; badge: string }> = {
  EN_ATTENTE:  { libelle: 'En attente',  badge: 'badge--orange' },
  EN_COURS:    { libelle: 'En cours',    badge: 'badge--bleu' },
  ACCEPTE:     { libelle: 'Accepté',     badge: 'badge--vert' },
  REFUSE:      { libelle: 'Refusé',      badge: 'badge--rouge' },
  EXPIRE:      { libelle: 'Expiré',      badge: 'badge--gris' },
};

@Component({
  selector: 'app-liste-devis',
  templateUrl: './liste-devis.component.html',
  styleUrls: ['./liste-devis.component.scss']
})
export class ListeDevisComponent implements OnInit {
  demandes: DemandeDevis[] = [];
  demandesFiltrees: DemandeDevis[] = [];
  recherche = '';
  filtreStatut = '';
  chargement = false;
  enCoursStatut: number | null = null;

  demandeSelectionnee: DemandeDevis | null = null;
  afficherDetail = false;

  readonly statutsListe = Object.keys(STATUTS);
  readonly STATUTS = STATUTS;

  private readonly apiUrl = `${environment.loginurl}devis`;

  constructor(private http: HttpClient, private toast: ToastService, private router: Router) {}

  ngOnInit(): void { this.charger(); }

  charger(): void {
    this.chargement = true;
    this.http.get<DemandeDevis[]>(this.apiUrl).subscribe({
      next: (data) => {
        this.demandes = Array.isArray(data) ? data.sort((a, b) => (b.id ?? 0) - (a.id ?? 0)) : [];
        this.appliquerFiltres();
        this.chargement = false;
      },
      error: () => { this.chargement = false; this.toast.erreur('Impossible de charger les demandes de devis'); }
    });
  }

  appliquerFiltres(): void {
    let result = [...this.demandes];
    const q = this.recherche.toLowerCase().trim();
    if (q) result = result.filter(d =>
      d.villeDepart?.toLowerCase().includes(q) ||
      d.villeArrivee?.toLowerCase().includes(q) ||
      d.user?.nom?.toLowerCase().includes(q) ||
      d.user?.prenom?.toLowerCase().includes(q) ||
      d.user?.email?.toLowerCase().includes(q)
    );
    if (this.filtreStatut) result = result.filter(d => d.statut === this.filtreStatut);
    this.demandesFiltrees = result;
  }

  changerStatut(d: DemandeDevis, statut: string): void {
    this.enCoursStatut = d.id;
    this.http.put<DemandeDevis>(`${this.apiUrl}/${d.id}/statut`, null, { params: { statut } }).subscribe({
      next: (updated) => {
        const idx = this.demandes.findIndex(x => x.id === d.id);
        if (idx >= 0) { this.demandes[idx] = { ...this.demandes[idx], statut }; }
        if (this.demandeSelectionnee?.id === d.id) this.demandeSelectionnee = { ...this.demandeSelectionnee, statut };
        this.appliquerFiltres();
        this.toast.succes(`Statut mis à jour : ${this.libelleStatut(statut)}`);
        this.enCoursStatut = null;
      },
      error: () => { this.toast.erreur('Erreur lors du changement de statut'); this.enCoursStatut = null; }
    });
  }

  ouvrirDetail(d: DemandeDevis): void { this.demandeSelectionnee = d; this.afficherDetail = true; }
  fermerDetail(): void { this.afficherDetail = false; this.demandeSelectionnee = null; }

  libelleStatut(s: string): string { return STATUTS[s]?.libelle ?? s; }
  badgeStatut(s: string): string   { return STATUTS[s]?.badge ?? 'badge--gris'; }
  nomClient(d: DemandeDevis): string {
    if (!d.user) return '—';
    return [d.user.prenom, d.user.nom].filter(Boolean).join(' ') || d.user.email || '—';
  }

  creerCommandeDepuisDevis(d: DemandeDevis): void {
    this.router.navigate(['/commande/enregistrer'], {
      queryParams: {
        villeDepart: d.villeDepart,
        villeArrivee: d.villeArrivee,
        devisId: d.id,
        clientId: d.user?.id
      }
    });
  }

  get enAttente():  number { return this.demandes.filter(d => d.statut === 'EN_ATTENTE').length; }
  get enCours():    number { return this.demandes.filter(d => d.statut === 'EN_COURS').length; }
  get acceptees():  number { return this.demandes.filter(d => d.statut === 'ACCEPTE').length; }
}
